import { ArrowLeftOutlined, DeleteOutlined } from '@ant-design/icons';
import {
    Button,
    Checkbox,
    Col,
    Form,
    Grid,
    Input,
    List,
    message,
    Radio,
    Row,
    Select,
    Space,
} from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { Store } from 'antd/lib/form/interface';
import axios, { CancelToken } from 'axios';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAxios from '../../../hooks/axios/useAxios';

import Loading from '../../../components/Loading';

import { topTitleContext } from '../../../contexts/TopTitle';
import { MethodList } from '../../../interfaces/Method';
import { EnRoles, listUsersRoles } from '../../../interfaces/Roles';
import { ESelection } from '../models';
import { ConfirmModal } from '../../../components/Layout';

type ParamsProps = {
    id: string;
};

const UpdateAdmin: React.FC = () => {
    const [form] = Form.useForm();
    const breakpoints = Grid.useBreakpoint();
    const [storeClientsSelected, setStoreClientsSelected] =
        React.useState<ESelection>(ESelection.ALL_STORES);
    const [stores, setStores] = React.useState<Store[]>([]);
    const [loadedStores, setLoadedStores] = React.useState<Store[]>([]);
    const [isChecked, setIsChecked] = React.useState<string[]>([]);
    const [isCheckAll, setIsCheckAll] = React.useState(false);
    const [openModal, setOpenModal] = useState(false);

    const {
        error,
        response: responseUpdate,
        loading: loadingUpdate,
        fetch: fetchUpdate,
    } = useAxios();
    const {
        response: responseDelete,
        loading: loadingDelete,
        fetch: fetchDelete,
    } = useAxios();
    const {
        response: responseGet,
        loading: loadingGet,
        fetch: fetchGet,
    } = useAxios();
    const {
        loading: loadingGetStores,
        fetch: fetchGetStores,
        response: getStoresResponse,
    } = useAxios();

    const { title, titleValue } = useContext(topTitleContext);
    const titlePage = 'Editar Usuário';
    useEffect(() => {
        titleValue(titlePage);
        return () => {
            titleValue('');
        };
    }, [title]);

    const navigate = useNavigate();
    const { id } = useParams<ParamsProps>();

    const toggleModal = () => setOpenModal((prevState) => !prevState);
    const getAdmin = useCallback(
        (token: CancelToken) => {
            const params = {
                method: MethodList.GET,
                url: `/subusers/get/${id}`,
                cancelToken: token,
            };
            fetchGet(params);
        },
        [id],
    );

    const onSubmit = () => {
        const values = form.getFieldsValue(true);
        const selectedStores = isCheckAll
            ? stores.map((item: Store) => item.id)
            : isChecked;
        if (selectedStores.length === 0) {
            return message.error('Nenhuma loja selecionada');
        }
        const params = {
            method: MethodList.PUT,
            url: `/subusers/${id}`,
            data: {
                email: values.email,
                permission: values.role,
                stores: selectedStores,
            },
        };
        fetchUpdate(params);
    };

    const handleDelete = async () => {
        const params = {
            method: MethodList.DELETE,
            url: `/subusers/${id}`,
        };
        fetchDelete(params);
        toggleModal();
    };

    const handleClick = (e: CheckboxChangeEvent) => {
        const { id, checked } = e.target;

        setIsChecked([...isChecked!, id!]);
        if (!checked) {
            setIsChecked(isChecked!.filter((item: any) => item !== id));
        }
    };

    const serializeStores = ({ data }: { data: any[] }) => {
        const storesArr = [] as any;
        data.forEach((store) => {
            const storeData = {
                storeId: store.id,
                storeName: store.name,
            };

            const checkStoresArr = storesArr.some((el: any) => {
                return el.storeId === storeData.storeId;
            });

            if (!checkStoresArr) {
                storesArr.push(storeData);
            }
        });
        return storesArr;
    };

    const getStores = React.useCallback(async (token: CancelToken) => {
        try {
            const options = {
                method: MethodList.GET,
                url: 'store',
                cancelToken: token,
                params: {
                    perPage: 50,
                },
            };

            const response = await fetchGetStores(options);
            if (response?.data.data.length > 0) {
                setStores([...response?.data.data]);
            }
        } catch (error: unknown) {
            if (axios.isAxiosError(error) && error.response?.status !== 401) {
                message.error('Um erro inesperado ocorreu');
            }
        }
    }, []);

    useEffect(() => {
        const cancelToken = axios.CancelToken;
        const source = cancelToken.source();
        getStores(source.token);
        return () => {
            setStores([]);
            source.cancel('Request Cancelada');
        };
    }, [getStores]);

    useEffect(() => {
        if (!getStoresResponse?.data) return;
        setLoadedStores(serializeStores(getStoresResponse.data));
    }, [getStoresResponse]);

    useEffect(() => {
        const cancelToken = axios.CancelToken;
        const source = cancelToken.source();
        getAdmin(source.token);
        return () => {
            source.cancel('Request Cancelada');
        };
    }, [getAdmin]);

    useEffect(() => {
        if (responseGet?.status && responseGet?.data) {
            form.setFieldsValue({
                email: responseGet.data?.user.email,
                role: responseGet.data?.type,
            });
        }
    }, [responseGet]);

    useEffect(() => {
        if (!responseGet?.status || !responseGet?.data) return;
        const { subStores } = responseGet.data;
        const checked = subStores?.map((item: any) => {
            const { store } = item;
            return store?.id;
        });
        setIsChecked(checked);
        if (subStores.length === stores.length) {
            setStoreClientsSelected(ESelection.ALL_STORES);
            form.setFieldsValue({
                stores_permission: ESelection.ALL_STORES,
            });
            setIsCheckAll(true);
        } else {
            setStoreClientsSelected(ESelection.SOME_STORES);
            form.setFieldsValue({
                stores_permission: ESelection.SOME_STORES,
            });
        }
    }, [responseGet, stores]);

    useEffect(() => {
        if (responseUpdate?.status && responseUpdate?.data) {
            message.success(`O usuário foi atualizado com sucesso`);
            form.resetFields();
            navigate('/shopmanagers/users/list');
        }
    }, [responseUpdate]);

    useEffect(() => {
        if (responseDelete?.status) {
            message.success(
                `O usuário com email "${form.getFieldValue(
                    'email',
                )}" foi deletado com sucesso`,
            );
            navigate('/shopmanagers/users/list');
        }
    }, [responseDelete]);

    useEffect(() => {
        if (error?.response) {
            const { errors } = error.response?.data;
            errors?.forEach((errorMessage: string) =>
                message.error(errorMessage),
            );
        } else if (error) {
            message.error(
                'Um erro inesperado aconteceu, tente novamente mais tarde',
            );
        }
    }, [error]);

    useEffect(() => {
        setIsCheckAll(isChecked.length === loadedStores.length);
        if (storeClientsSelected === ESelection.ALL_STORES) {
            setIsCheckAll(true);
        }
    }, [loadedStores, isChecked, storeClientsSelected]);

    return (
        <Row style={{ padding: '0 2rem' }}>
            <Col span={8} xs={24} sm={20} md={14} lg={12} xl={12} xxl={10}>
                <Form form={form} onFinish={onSubmit} layout="vertical">
                    <Form.Item
                        label="E-mail"
                        name="email"
                        rules={[
                            { required: true, max: 255 },
                            {
                                type: 'email',
                                message: 'Por favor informe um email válido',
                            },
                        ]}
                    >
                        <Input disabled type="email" />
                    </Form.Item>
                    <Form.Item
                        label="Permissão"
                        name="role"
                        rules={[
                            {
                                required: true,
                                type: 'enum',
                                enum: listUsersRoles(),
                            },
                        ]}
                    >
                        <Select
                            options={[
                                {
                                    label: 'Usuário Lojista',
                                    value: EnRoles.restocker,
                                },
                                {
                                    label: 'Usuário Síndico',
                                    value: EnRoles.trustee,
                                },
                            ]}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Permissão de acesso a lojas"
                        name="stores_permission"
                        rules={[
                            {
                                required: true,
                                message: 'Selecione as lojas',
                            },
                        ]}
                    >
                        <Radio.Group
                            onChange={(v) =>
                                setStoreClientsSelected(v.target.value)
                            }
                            value={storeClientsSelected}
                        >
                            <Space orientation="vertical">
                                <Radio value={ESelection.ALL_STORES}>
                                    Todas as lojas
                                </Radio>
                                <Radio value={ESelection.SOME_STORES}>
                                    Lojas específicas
                                </Radio>
                                {storeClientsSelected ===
                                    ESelection.SOME_STORES && (
                                    <Col
                                        style={{
                                            padding: '0 16px',
                                            height: 150,
                                            width: '100%',
                                            overflow: 'auto',
                                            border: '1px solid rgba(140, 140, 140, 0.35)',
                                        }}
                                    >
                                        {loadingGet && loadedStores ? (
                                            <Loading size="large" />
                                        ) : (
                                            <>
                                                <Form.Item>
                                                    <List
                                                        itemLayout="vertical"
                                                        dataSource={
                                                            loadedStores
                                                        }
                                                        renderItem={(
                                                            store: any,
                                                        ) => (
                                                            <>
                                                                <List.Item
                                                                    key={
                                                                        store.storeId
                                                                    }
                                                                >
                                                                    <Checkbox
                                                                        onChange={
                                                                            handleClick
                                                                        }
                                                                        checked={isChecked!.includes(
                                                                            store.storeId,
                                                                        )}
                                                                        id={
                                                                            store.storeId
                                                                        }
                                                                    >
                                                                        {
                                                                            store.storeName
                                                                        }
                                                                    </Checkbox>
                                                                </List.Item>
                                                            </>
                                                        )}
                                                    />
                                                </Form.Item>
                                            </>
                                        )}
                                    </Col>
                                )}
                            </Space>
                        </Radio.Group>
                    </Form.Item>
                    <Row justify="end">
                        <Col xs={24} sm={8} md={8} lg={8} xl={8} xxl={8}>
                            <Button
                                danger
                                style={{ width: '100%' }}
                                icon={<DeleteOutlined />}
                                onClick={toggleModal}
                            >
                                Excluir
                            </Button>
                        </Col>
                    </Row>
                    <Row justify="center" gutter={[0, 10]}>
                        <Col xs={24} sm={8} md={8} lg={8} xl={8} xxl={8}>
                            <Button
                                htmlType="reset"
                                icon={<ArrowLeftOutlined />}
                                onClick={() => navigate(-1)}
                                style={{ width: '100%' }}
                            >
                                Voltar
                            </Button>
                        </Col>
                        <Col
                            xs={{ span: 24, offset: 0 }}
                            sm={{ span: 8, offset: 1 }}
                            md={{ span: 8, offset: 1 }}
                            lg={{ span: 8, offset: 1 }}
                            xl={{ span: 8, offset: 1 }}
                            xxl={{ span: 8, offset: 1 }}
                        >
                            <Button
                                htmlType="submit"
                                className="default-btn"
                                loading={loadingUpdate}
                                style={{
                                    width: breakpoints.sm ? undefined : '100%',
                                }}
                            >
                                {loadingUpdate ? 'Salvando...' : 'Salvar'}
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Col>
            <ConfirmModal
                title="Excluir Usuário"
                visible={openModal}
                onOk={handleDelete}
                onCancel={toggleModal}
                okButtonProps={{ loading: loadingDelete }}
            />
        </Row>
    );
};

export default UpdateAdmin;
